'use client';

import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { usePrefiereMovimientoReducido } from '@/components/asistente/movimientoReducido';
import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import MarcoEscalado from '@/components/marketing/comun/MarcoEscalado';
import { PANTALLA_INICIAL, PANTALLAS_APP, type PantallaApp } from '@/content/pantallasApp';
import { cn } from '@/lib/utils';

import { TEXTOS_SECCION_MOCKUPS as TEXTOS, textoPosicion } from './textosSeccionMockups';

interface PropiedadesCarruselPantallas {
    /** Las siete pantallas móviles ya pintadas en el servidor, en el orden de PANTALLAS_APP. */
    paneles: ReactNode[];
    /** El CTA de ancho completo, también de servidor. */
    cta: ReactNode;
}

const ID_PANEL = 'pantalla-panel-movil';
const TOTAL = PANTALLAS_APP.length;
// El chip elegido queda a 16 px del borde, el mismo margen que el texto de la página.
const MARGEN_CHIP = 16;
// La pantalla móvil mide 358×560 y su sombra dura suma 4 px: el escenario la incluye para no recortarla.
const ANCHO_ESCENARIO = 362;
const ALTO_ESCENARIO = 564;

function idChip(pantalla: PantallaApp): string {
    return `pantalla-tab-${pantalla.numero}-movil`;
}

function vuelta(indice: number): number {
    return (indice + TOTAL) % TOTAL;
}

function destinoDeTecla(tecla: string, indice: number): number | null {
    const saltos: Record<string, number> = { ArrowRight: indice + 1, ArrowLeft: indice - 1, Home: 0, End: TOTAL - 1 };
    const destino = saltos[tecla];
    return destino === undefined ? null : vuelta(destino);
}

/** Lleva el chip a la vista dentro de su fila; en 390 px solo caben cuatro. */
function desplazarFila(fila: HTMLElement | null, chip: HTMLElement | null, reducido: boolean) {
    if (!fila || !chip || typeof fila.scrollTo !== 'function') return;
    fila.scrollTo({ left: Math.max(0, chip.offsetLeft - MARGEN_CHIP), behavior: reducido ? 'auto' : 'smooth' });
}

/**
 * El estado del carrusel. La fila se desplaza dentro del cambio que pide el usuario y no en un
 * efecto: el lienzo solo la mueve cuando el usuario cambia de pantalla, nunca al montar.
 */
function useCarrusel() {
    const [elegida, setElegida] = useState(PANTALLA_INICIAL - 1);
    const reducido = usePrefiereMovimientoReducido();
    const fila = useRef<HTMLDivElement>(null);
    const chips = useRef<(HTMLButtonElement | null)[]>([]);

    const ir = (indice: number) => {
        const destino = vuelta(indice);
        setElegida(destino);
        desplazarFila(fila.current, chips.current[destino], reducido);
        return destino;
    };
    return { elegida, ir, fila, chips };
}

interface PropiedadesChip {
    pantalla: PantallaApp;
    elegido: boolean;
    alElegir: () => void;
    alPulsarTecla: (evento: KeyboardEvent<HTMLButtonElement>) => void;
    refBoton: (nodo: HTMLButtonElement | null) => void;
}

function Chip({ pantalla, elegido, alElegir, alPulsarTecla, refBoton }: PropiedadesChip) {
    return (
        <button
            ref={refBoton}
            type="button"
            role="tab"
            id={idChip(pantalla)}
            aria-selected={elegido}
            aria-controls={ID_PANEL}
            tabIndex={elegido ? 0 : -1}
            onClick={alElegir}
            onKeyDown={alPulsarTecla}
            className={cn(
                'h-11 shrink-0 whitespace-nowrap rounded-full border-2 border-ink px-[14px] font-sans text-[14px]',
                elegido ? 'bg-accent-blue font-bold text-on-accent-blue shadow-ds-chip' : 'bg-surface-2 font-semibold text-ink hover:border-ink/40',
            )}
        >
            {pantalla.corto}
        </button>
    );
}

function Flecha({ trazo }: { trazo: string }) {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={trazo} />
        </svg>
    );
}

function Puntos({ elegida }: { elegida: number }) {
    return (
        <span data-puntos aria-hidden="true" className="flex items-center gap-1.5">
            {PANTALLAS_APP.map((pantalla, indice) => (
                <span key={pantalla.numero} className={cn('h-2 rounded-[4px]', indice === elegida ? 'w-[22px] bg-ink' : 'w-2 bg-ink/25')} />
            ))}
        </span>
    );
}

function Controles({ elegida, ir }: { elegida: number; ir: (indice: number) => void }) {
    const base = 'flex size-11 items-center justify-center rounded-ds-md border-2 border-ink p-0';
    return (
        <div className="flex items-center justify-between">
            <button type="button" aria-label={TEXTOS.anterior} onClick={() => ir(elegida - 1)} className={cn(base, 'bg-surface-2 text-ink')}>
                <Flecha trazo="M10 3L5 8l5 5" />
            </button>
            <Puntos elegida={elegida} />
            <button type="button" aria-label={TEXTOS.siguiente} onClick={() => ir(elegida + 1)} className={cn(base, 'bg-accent-blue text-on-accent-blue')}>
                <Flecha trazo="M6 3l5 5-5 5" />
            </button>
            <p aria-live="polite" className="sr-only">
                {textoPosicion(elegida + 1, TOTAL)}
            </p>
        </div>
    );
}

function TarjetaFijate({ texto }: { texto: string }) {
    return (
        <div className="flex flex-col gap-1.5 rounded-[14px] border-2 border-ink bg-surface-2 px-[14px] py-3 shadow-ds-2">
            <span className="flex h-[22px] items-center self-start rounded-full border-2 border-ink bg-accent-blue px-[9px] text-[12px] font-bold text-on-accent-blue">
                {TEXTOS.etiquetaFijate}
            </span>
            <span data-fijate className="text-[14px] leading-[1.5] text-ink">
                {texto}
            </span>
        </div>
    );
}

function PanelMovil({ paneles, elegida, ir }: { paneles: ReactNode[]; elegida: number; ir: (indice: number) => void }) {
    const pantalla = PANTALLAS_APP[elegida];
    return (
        <div role="tabpanel" id={ID_PANEL} aria-labelledby={idChip(pantalla)} className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
                <strong className="font-display text-[19px] leading-[1.25] text-ink">{pantalla.titulo}</strong>
                <span className="text-[14px] text-ink/90">{pantalla.frase}</span>
            </div>
            <div className="w-[calc(100%+4px)] max-w-[362px]">
                <MarcoEscalado anchoDiseno={ANCHO_ESCENARIO} altoDiseno={ALTO_ESCENARIO} escalaInicial={1}>
                    {paneles.map((panel, indice) => (
                        <div key={PANTALLAS_APP[indice]?.numero ?? indice} data-panel hidden={indice !== elegida}>
                            {panel}
                        </div>
                    ))}
                </MarcoEscalado>
            </div>
            <Controles elegida={elegida} ir={ir} />
            <TarjetaFijate texto={pantalla.fijate} />
            <LeyendaIlustracion texto={TEXTOS.leyenda} />
        </div>
    );
}

/**
 * El carrusel móvil de Seccion_Mockups_Movil (UI-SPEC §Interaction 3 «Mobile»): chips con
 * desplazamiento horizontal, anterior/siguiente con vuelta y una región viva con la posición.
 * Sin gesto de deslizar ni avance automático, porque el tablero no dibuja ninguno.
 */
export default function CarruselPantallas({ paneles, cta }: PropiedadesCarruselPantallas) {
    const { elegida, ir, fila, chips } = useCarrusel();

    const alPulsarTecla = (evento: KeyboardEvent<HTMLButtonElement>, indice: number) => {
        const destino = destinoDeTecla(evento.key, indice);
        if (destino === null) return;
        evento.preventDefault();
        // Sin preventScroll, el salto propio del foco cortaría el desplazamiento suave de la fila.
        chips.current[ir(destino)]?.focus({ preventScroll: true });
    };

    return (
        <div className="mt-4 flex flex-col gap-4">
            <div ref={fila} role="tablist" aria-label={TEXTOS.etiquetaLista} className="no-scrollbar relative -mx-4 flex flex-nowrap gap-2 overflow-x-auto px-4 pb-[6px] pt-[2px]">
                {PANTALLAS_APP.map((pantalla, indice) => (
                    <Chip
                        key={pantalla.numero}
                        pantalla={pantalla}
                        elegido={indice === elegida}
                        alElegir={() => ir(indice)}
                        alPulsarTecla={(evento) => alPulsarTecla(evento, indice)}
                        refBoton={(nodo) => {
                            chips.current[indice] = nodo;
                        }}
                    />
                ))}
            </div>
            <PanelMovil paneles={paneles} elegida={elegida} ir={ir} />
            {cta}
        </div>
    );
}
